/*
 * DataTable: a list with a title and count, filter chips, search, sortable headers and rows that open a record. On phones
 * each row becomes a card (columns show as labelled lines).
 *
 * Optional: tick boxes on each row with a bar of actions for the ticked ones, a search the server answers (query), and
 * loading and error states in place of the rows.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Icon } from "./Icon";

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  /** The value to sort by; columns without it do not sort. */
  sort?: (row: T) => string | number;
  align?: "right";
}

export function DataTable<T>({
  title,
  rows,
  columns,
  rowKey,
  onRowClick,
  search,
  query,
  tools,
  empty,
  loading,
  error,
  selected,
  onSelect,
  bulk,
  rowClass,
  serverSort,
  total,
  count,
  footer,
  phoneCard,
}: {
  title: string;
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  /** The text each row is searched by, here in the browser. */
  search?: (row: T) => string;
  /** A search the server answers: the typed words and where they go. */
  query?: { value: string; onChange: (v: string) => void; placeholder?: string };
  tools?: ReactNode;
  empty?: ReactNode;
  loading?: boolean;
  error?: ReactNode;
  /** Ticked rows, by key. With onSelect, each row gets a tick box. */
  selected?: ReadonlySet<string>;
  onSelect?: (keys: Set<string>) => void;
  /** The actions for ticked rows, shown in a bar over the list while any are ticked. */
  bulk?: ReactNode;
  rowClass?: (row: T) => string | undefined;
  /** Sorting the server does (the list comes a page at a time): the columns it sorts by, the current one, and a header click. */
  serverSort?: { keys: readonly string[]; key: string; dir: "asc" | "desc"; onSort: (key: string) => void };
  /** The whole count when only part of the list has loaded. */
  total?: number | undefined;
  /** The count beside the title, when the rows are only the pages loaded so far. */
  count?: ReactNode;
  /** Under the rows: Show more for a list that loads a page at a time. */
  footer?: ReactNode;
  /** On a phone, each row as this short card instead of every column with its label (owner, 2026-10-31: "super fat"). */
  phoneCard?: (row: T) => ReactNode;
}) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);
  const shown = useMemo(() => {
    const term = q.trim().toLowerCase();
    let out = term && search ? rows.filter((r) => search(r).toLowerCase().includes(term)) : rows;
    const col = sort ? columns.find((c) => c.key === sort.key) : undefined;
    if (col?.sort && sort) {
      const by = col.sort;
      out = [...out].sort((a, b) => {
        const x = by(a);
        const y = by(b);
        return (typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y))) * sort.dir;
      });
    }
    return out;
  }, [rows, q, sort, columns, search]);

  const ticks = Boolean(onSelect && selected);
  const ticked = selected?.size ?? 0;
  const allTicked = ticks && shown.length > 0 && shown.every((r) => selected?.has(rowKey(r)));
  const toggle = (key: string) => {
    if (!onSelect || !selected) return;
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelect(next);
  };
  const toggleAll = () => {
    if (!onSelect) return;
    onSelect(allTicked ? new Set() : new Set(shown.map(rowKey)));
  };
  const typed = query ? query.value : q;

  return (
    <section className="card">
      {ticks && ticked > 0 ? (
        <div className="bulk-bar" role="region" aria-label="Ticked rows">
          <b className="num">{ticked} selected</b>
          <button type="button" className="btn tertiary small" onClick={() => onSelect?.(new Set())}>
            Clear
          </button>
          <div className="spacer" />
          {bulk}
        </div>
      ) : (
        <div className="table-tools">
          <h2>
            {title}{" "}
            <span className="num">
              ({count ?? `${String(total ?? shown.length)} ${(total ?? shown.length) === 1 ? "result" : "results"}`})
            </span>
          </h2>
          <div className="row">
            {tools}
            {search || query ? (
              <div className="search" style={{ width: 240, flex: "none", maxWidth: "100%" }}>
                <Icon name="search" size={18} />
                <input
                  aria-label={`Search ${title.toLowerCase()}`}
                  placeholder={query?.placeholder ?? "Search"}
                  value={typed}
                  onChange={(e) => (query ? query.onChange(e.target.value) : setQ(e.target.value))}
                />
              </div>
            ) : null}
          </div>
        </div>
      )}
      {error ? (
        <div className="table-empty" role="alert">
          {error}
        </div>
      ) : loading && !rows.length ? (
        <div className="skeleton-rows" aria-busy="true" aria-label="Loading">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton" />
          ))}
        </div>
      ) : shown.length ? (
        <div className={`table-wrap${loading ? " refreshing" : ""}`}>
          <table className={phoneCard ? "data cards has-phone-card" : "data cards"}>
            <thead>
              <tr>
                {ticks ? (
                  <th className="tick">
                    <input
                      type="checkbox"
                      aria-label={allTicked ? "Untick all" : "Tick all"}
                      checked={allTicked}
                      onChange={toggleAll}
                    />
                  </th>
                ) : null}
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={c.align === "right" ? "right" : undefined}
                    aria-sort={
                      serverSort
                        ? serverSort.key === c.key
                          ? serverSort.dir === "asc"
                            ? "ascending"
                            : "descending"
                          : undefined
                        : sort?.key === c.key
                          ? sort.dir === 1
                            ? "ascending"
                            : "descending"
                          : undefined
                    }
                  >
                    {serverSort?.keys.includes(c.key) ? (
                      <button type="button" onClick={() => serverSort.onSort(c.key)}>
                        {c.header}
                        {serverSort.key === c.key ? <span aria-hidden>{serverSort.dir === "asc" ? "↑" : "↓"}</span> : null}
                      </button>
                    ) : c.sort && !serverSort ? (
                      <button
                        type="button"
                        onClick={() =>
                          setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === 1 ? -1 : 1 } : { key: c.key, dir: 1 }))
                        }
                      >
                        {c.header}
                        {sort?.key === c.key ? <span aria-hidden>{sort.dir === 1 ? "↑" : "↓"}</span> : null}
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const key = rowKey(r);
                const cls = [onRowClick ? "clickable" : "", selected?.has(key) ? "ticked" : "", rowClass?.(r) ?? ""]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <tr
                    key={key}
                    className={cls || undefined}
                    onClick={onRowClick ? () => onRowClick(r) : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={onRowClick ? (e) => e.key === "Enter" && onRowClick(r) : undefined}
                  >
                    {ticks ? (
                      <td className="tick" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label="Tick this row"
                          checked={selected?.has(key) ?? false}
                          onChange={() => toggle(key)}
                        />
                      </td>
                    ) : null}
                    {columns.map((c) => (
                      <td key={c.key} className={c.align === "right" ? "right" : undefined} data-label={c.header || undefined}>
                        {c.render(r)}
                      </td>
                    ))}
                    {phoneCard ? <td className="phone-card">{phoneCard(r)}</td> : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {footer}
        </div>
      ) : (
        <div className="table-empty">
          {typed ? "Nothing matches that search. Try fewer letters." : (empty ?? "Nothing here yet.")}
        </div>
      )}
    </section>
  );
}

/**
 * Under a list that loads a page at a time: the next page loads on its own as this comes into view, and Show more does
 * the same by hand (owner, 2026-10-29, Jobber's Jobs list "loads like 20 at a time and when you scroll it loads in more").
 */
export function MoreRows({ loading, onMore }: { loading: boolean; onMore: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const more = useRef(onMore);
  more.current = onMore;
  useEffect(() => {
    const el = box.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const watch = new IntersectionObserver((seen) => seen.some((e) => e.isIntersecting) && more.current(), {
      rootMargin: "300px",
    });
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  return (
    <div className="more-rows" ref={box}>
      <button type="button" className="btn tertiary small" disabled={loading} onClick={onMore}>
        {loading ? "Loading more" : "Show more"}
      </button>
    </div>
  );
}
